import { t as __t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CopyrightFooter from "../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../components/header/DashboardSidebar";
import MobileMenu from "../../components/header/MobileMenu";
import { FormatCurrencys, getDeviceId } from "../../utils/commonUtil";
import "../../components/i18n/lang";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { callDashboard } from "../../api/users/service";
import { getDashboardContractList } from "../../api/insurances/service";
import RecordsTableFilter from "../../components/table-filter";
import CustomPagination from "../../components/paginations";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SwalAlert from "../../components/common/alert";
import "./style.css";
import SmartTable from "../../components/common/table-component";

const DEFAULT_REQUEST = {
  page: 1,
  maxResult: 10,
  keyword: "",
  fromDate: "",
  endDate: "",
};

const safeNumber = (value) => Number(value || 0);

const Index = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.dataLogin);

  const [dashboard, setDashboard] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [request, setRequest] = useState(DEFAULT_REQUEST);
  const [filterDraft, setFilterDraft] = useState({
    keyword: "",
    startDate: null,
    endDate: null,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const agentId = userInfo?.data?.agent?.agentId;
  const userTypeId = userInfo?.data?.user?.userTypeId;
  const roleStaff = userInfo?.data?.user?.roleStaff;

  useEffect(() => {
    let storedDeviceId = localStorage.getItem("deviceId");
    if (!storedDeviceId) {
      storedDeviceId = uuidv4();
      localStorage.setItem("deviceId", storedDeviceId);
    }
    getDeviceId();
  }, []);

  useEffect(() => {
    if (userTypeId === 3 && !agentId) {
      SwalAlert.error("", __t("uiText.text297"));
      return;
    }
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, roleStaff, userTypeId]);

  useEffect(() => {
    if (userTypeId === 3 && !agentId) return;
    loadContractList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.page, request.maxResult, request.keyword, request.fromDate, request.endDate, agentId]);

  const loadSummary = async (filters = request) => {
    try {
      const rsp = await dispatch(
        callDashboard({
          page: 1,
          maxResult: 10,
          startDate: filters?.fromDate || null,
          endDate: filters?.endDate || null,
          type: roleStaff,
          agentId,
        })
      );

      if (rsp?.payload?.responseCode === "00000") {
        setDashboard(rsp?.payload?.data || null);
      }
    } catch (error) {
      console.error("Dashboard summary error:", error);
    }
  };

  const loadContractList = async () => {
    setListLoading(true);
    setListError("");

    try {
      // Dashboard originally depended on `listExprite` from /base/dashboard/get.
      // That field is not reliable across API versions. The report endpoint below
      // is the canonical source already used by the Near Expired report page.
      const rsp = await getDashboardContractList({
        page: request.page,
        maxResult: request.maxResult,
        keyword: request.keyword,
        fromDate: request.fromDate,
        endDate: request.endDate,
        agentId,
      });

      if (rsp?.responseCode === "00000") {
        const data = rsp?.data || {};
        setContracts(Array.isArray(data?.result) ? data.result : []);
        setTotalPages(Math.max(1, safeNumber(data?.totalPages) || 1));
        setTotalRecord(safeNumber(data?.totalRecords));
        return;
      }

      // Compatibility fallback for an older dashboard API response.
      const legacy = dashboard?.listExprite;
      if (Array.isArray(legacy?.result)) {
        setContracts(legacy.result);
        setTotalPages(Math.max(1, safeNumber(legacy?.totalPages) || 1));
        setTotalRecord(safeNumber(legacy?.totalRecords));
      } else {
        setContracts([]);
        setTotalPages(1);
        setTotalRecord(0);
        setListError(rsp?.responseDescription || t("error"));
      }
    } catch (error) {
      console.error("Dashboard contract list error:", error);

      const legacy = dashboard?.listExprite;
      if (Array.isArray(legacy?.result)) {
        setContracts(legacy.result);
        setTotalPages(Math.max(1, safeNumber(legacy?.totalPages) || 1));
        setTotalRecord(safeNumber(legacy?.totalRecords));
      } else {
        setContracts([]);
        setTotalPages(1);
        setTotalRecord(0);
        setListError(error?.message || t("error"));
      }
    } finally {
      setListLoading(false);
    }
  };

  const stats = useMemo(
    () => [
      {
        key: "all",
        icon: "flaticon-briefcase",
        value: safeNumber(dashboard?.getAll?.count),
        label: t("dashboard.allContract"),
        tone: "blue",
      },
      {
        key: "active",
        icon: "la-bookmark-o",
        value: safeNumber(dashboard?.workingContract?.count),
        label: t("dashboard.activeContract"),
        tone: "green",
      },
      {
        key: "expired",
        icon: "la-hourglass-half",
        value: safeNumber(dashboard?.countExprite?.count),
        label: t("dashboard.nearlyExpired"),
        tone: "orange",
      },
      {
        key: "claim",
        icon: "la-file-invoice",
        value: safeNumber(dashboard?.claimContract?.count),
        label: t("dashboard.claim"),
        tone: "red",
      },
    ],
    [dashboard, t]
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setRequest((prev) => ({ ...prev, page: pageNumber }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextRequest = {
      ...request,
      page: 1,
      keyword: filterDraft.keyword.trim(),
      fromDate: filterDraft.startDate
        ? moment(filterDraft.startDate).format("YYYY-MM-DD")
        : "",
      endDate: filterDraft.endDate
        ? moment(filterDraft.endDate).format("YYYY-MM-DD")
        : "",
    };
    setRequest(nextRequest);
    loadSummary(nextRequest);
  };

  const handleClear = () => {
    const nextRequest = {
      ...request,
      page: 1,
      keyword: "",
      fromDate: "",
      endDate: "",
    };
    setFilterDraft({ keyword: "", startDate: null, endDate: null });
    setRequest(nextRequest);
    loadSummary(nextRequest);
  };

  const getContractNo = (item) => item?.contractNo || item?.contractNumber || "-";

  const getCustomerName = (item) => {
    const fullName = [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim();
    return fullName || item?.customerName || "-";
  };

  const getAgentName = (item) => {
    const name = [item?.agentName, item?.agentLastName].filter(Boolean).join(" ").trim();
    return name || "-";
  };

  const getRemainingDays = (expiredDate) => {
    if (!expiredDate || !moment(expiredDate).isValid()) return null;
    return moment(expiredDate).startOf("day").diff(moment().startOf("day"), "days");
  };

  const renderRemaining = (expiredDate) => {
    const days = getRemainingDays(expiredDate);
    if (days === null) return <span className="dashboard-muted">-</span>;
    if (days < 0) {
      return <span className="dashboard-status dashboard-status-expired">{__t("uiText.text298")}</span>;
    }
    if (days === 0) {
      return <span className="dashboard-status dashboard-status-danger">{__t("uiText.text299")}</span>;
    }
    return <span className="dashboard-status dashboard-status-warning">{__t("uiText.text300")}{days} {__t("uiText.text301")}</span>;
  };

  return (
    <div className="page-wrapper dashboard dashboard-v2">
      <span className="header-span"></span>

      <DashboardHeader />
      <MobileMenu />
      <DashboardEmployerSidebar />

      <section className="user-dashboard">
        <div className="dashboard-outer dashboard-v2-outer">
          <div className="dashboard-hero">
            <div>
              <div className="dashboard-eyebrow">{__t("uiText.text427")}</div>
              <h2>{t("menu.home")}</h2>
              <p>{__t("uiText.text302")}</p>
            </div>
            <button className="dashboard-refresh-btn" type="button" onClick={() => { loadSummary(); loadContractList(); }}>
              <i className="la la-refresh"></i>
              <span>{__t("uiText.text303")}</span>
            </button>
          </div>

          <div className="row dashboard-stat-grid">
            {stats.map((stat) => (
              <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12" key={stat.key}>
                <div className={`dashboard-stat-card dashboard-stat-${stat.tone}`}>
                  <div className="dashboard-stat-icon">
                    <i className={`icon la ${stat.icon}`}></i>
                  </div>
                  <div className="dashboard-stat-content">
                    <div className="dashboard-stat-value">{FormatCurrencys(stat.value) || 0}</div>
                    <div className="dashboard-stat-label">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="dashboard-panel">
            <div className="dashboard-panel-header">
              <div>
                <div className="dashboard-panel-kicker">{__t("uiText.text428")}</div>
                <h3>{t("dashboard.nearlyExpired")}</h3>
                <p>{__t("uiText.text304")}</p>
              </div>
              <div className="dashboard-record-pill">
                <span>{FormatCurrencys(totalRecord) || 0}</span>
                <small>{__t("uiText.text040")}</small>
              </div>
            </div>

            <form className="dashboard-filter-bar" onSubmit={handleSubmit}>
              <div className="dashboard-filter-field dashboard-filter-search">
                <label>{t("search.keyword")}</label>
                <div className="dashboard-input-wrap">
                  <i className="la la-search"></i>
                  <input
                    type="text"
                    value={filterDraft.keyword}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, keyword: e.target.value }))}
                    placeholder={__t("uiText.text305")}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="dashboard-filter-field">
                <label>{t("startDate")}</label>
                <DatePicker
                  selected={filterDraft.startDate}
                  onChange={(date) => setFilterDraft((prev) => ({ ...prev, startDate: date }))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t("startDate")}
                  className="dashboard-date-input"
                  isClearable
                />
              </div>

              <div className="dashboard-filter-field">
                <label>{t("endDate")}</label>
                <DatePicker
                  selected={filterDraft.endDate}
                  onChange={(date) => setFilterDraft((prev) => ({ ...prev, endDate: date }))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t("endDate")}
                  className="dashboard-date-input"
                  minDate={filterDraft.startDate || undefined}
                  isClearable
                />
              </div>

              <div className="dashboard-filter-actions">
                <button type="button" className="dashboard-btn dashboard-btn-light" onClick={handleClear}>
                  <i className="la la-times"></i> {t("button.clear")}
                </button>
                <button type="submit" className="dashboard-btn dashboard-btn-primary">
                  <i className="la la-search"></i> {t("button.search")}
                </button>
              </div>
            </form>

            <div className="dashboard-table-toolbar">
              <RecordsTableFilter
                totalRecord={totalRecord}
                perPage={request.maxResult}
                onChange={(e) =>
                  setRequest((prev) => ({
                    ...prev,
                    maxResult: Number(e.target.value),
                    page: 1,
                  }))
                }
              />
            </div>

            <div className="dashboard-table-wrap">
              {listLoading ? (
                <div className="dashboard-empty-state">
                  <div className="dashboard-spinner"></div>
                  <strong>{__t("uiText.text047")}</strong>
                </div>
              ) : listError ? (
                <div className="dashboard-empty-state dashboard-error-state">
                  <i className="la la-exclamation-circle"></i>
                  <strong>{__t("uiText.text306")}</strong>
                  <span>{listError}</span>
                  <button type="button" onClick={loadContractList}>{__t("uiText.text307")}</button>
                </div>
              ) : (
                <SmartTable
                  columns={[
                    { labelKey: "contracts.vehicle.contractNumber", key: "contractNo", render: (_, row) => <strong className="dashboard-contract-no">{getContractNo(row)}</strong> },
                    { labelKey: "contracts.vehicle.customerId", key: "customerId" },
                    { labelKey: "contracts.vehicle.customerName", key: "customerName", render: (_, row) => getCustomerName(row) },
                    { labelKey: "contracts.vehicle.agentCode", key: "agentCode", render: (val, row) => <div className="dashboard-agent-cell"><strong>{val || "-"}</strong><small>{getAgentName(row)}</small></div> },
                    { labelKey: "contracts.vehicle.signedDate", key: "signedDate", render: (val) => val ? moment(val).format("DD/MM/YYYY") : "-" },
                    { labelKey: "contracts.vehicle.expiredDate", key: "expiredDate", render: (val) => <strong>{val ? moment(val).format("DD/MM/YYYY") : "-"}</strong> },
                    { label: t("uiLabels.status"), key: "remaining", render: (_, row) => renderRemaining(row?.expiredDate) },
                  ]}
                  data={{ result: contracts }}
                  rowKey="id"
                  recordsTableFilter={false}
                  customPagination={false}
                  showRowNumber={true}
                  rowNumberOffset={(request.page - 1) * request.maxResult}
                />
              )}
            </div>

            <div className="dashboard-pagination-wrap">
              <CustomPagination
                currentPage={request.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={5}
              />
            </div>
          </div>
        </div>
      </section>

      <CopyrightFooter />
    </div>
  );
};

export default Index;
